/**
 * Public partner-map data — the pins and results list behind the PartnerMap
 * section (see `partners.public_views.PartnerMapView`). Same-origin fetch, not
 * `apiClient`: this is an AllowAny, section-gated endpoint reached by
 * anonymous site visitors, the same posture as `api/states.ts`'s
 * `fetchStateDetailMap`.
 */
import { NotFound, StateMapData } from './states';

export type PartnerPin = {
  id: number;
  school_name: string;
  street: string;
  city: string;
  /** The free-text state the school typed — the directory's search matches it. */
  state: string;
  longitude: number;
  latitude: number;
  /** Same-origin path to the partner's own PageTemplate page. */
  url: string;
  /** Present only when the caller passed `filterField` and it's still a
   * live choice-type field on the tenant's form. */
  filter_value?: string;
};

export type PartnerMapData = {
  pins: PartnerPin[];
  /** Only on the directory's response: the base map to draw `pins` on — the
   * overview's states plus a polygon for every state holding a pin, framed to
   * take in the footprint and every pin. No `features` means nothing to draw. */
  map?: StateMapData;
  /** The chosen filter field's own label (e.g. "School Type"), for the
   * visitor-facing filter control. Absent when no filter field resolved. */
  filter_field_label?: string;
};

/**
 * Published, geocoded partners inside one state. `filterField` is a
 * FormField name (see `partnersApi.filterableFields`); omit it to skip the
 * per-pin `filter_value`/`filter_field_label` entirely.
 */
export function fetchPartnerMap(
  abbreviation: string,
  filterField: string | undefined,
  signal?: AbortSignal,
): Promise<PartnerMapData> {
  const query = filterField ? `?filter_field=${encodeURIComponent(filterField)}` : '';
  const url = `/api/partners/${abbreviation.toLowerCase()}/map/${query}`;
  return fetch(url, { signal, headers: { Accept: 'application/json' } }).then((response) => {
    if (response.status === 404) {
      throw new NotFound(url);
    }
    if (!response.ok) {
      throw new Error(`${url} responded ${response.status}`);
    }
    return response.json();
  });
}

/**
 * Every published, geocoded partner in the tenant — the `PartnerDirectory`
 * section's data (`partners.public_views.PartnerDirectoryView`). Answers only
 * while an active directory section is showing somewhere, so a 404 means "no
 * directory is configured", not a failure.
 */
export function fetchPartnerDirectory(
  filterField: string | undefined,
  signal?: AbortSignal,
): Promise<PartnerMapData> {
  const query = filterField ? `?filter_field=${encodeURIComponent(filterField)}` : '';
  const url = `/api/partners/directory/${query}`;
  return fetch(url, { signal, headers: { Accept: 'application/json' } }).then((response) => {
    if (response.status === 404) {
      throw new NotFound(url);
    }
    if (!response.ok) {
      throw new Error(`${url} responded ${response.status}`);
    }
    return response.json();
  });
}
